import { useState } from 'react';
import { AlertCircle, Download, FileSpreadsheet, Lock, RefreshCw } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { isBuilding, useOrderSample, useOrderSampleStores } from '@/hooks/use-order-samples';
import { SHEET_LIFETIME_MS, formatIst, isShort, storeLabel } from '../test-orders';
import type { OrderSampleJob, OrderSampleStatusCount } from '@/types';

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className='rounded-md bg-muted/50 p-3'>
      <p className='text-xs text-muted-foreground'>{label}</p>
      <p className='text-2xl font-medium'>{value.toLocaleString()}</p>
    </div>
  );
}

function StatusList({ statuses }: { statuses: OrderSampleStatusCount[] }) {
  return (
    <div className='text-sm'>
      <div className='grid grid-cols-[minmax(0,1fr)_11rem_4rem] gap-2 pb-2 text-xs text-muted-foreground'>
        <span>Status</span>
        <span>Shopify value</span>
        <span className='text-right'>Orders</span>
      </div>
      <ul>
        {statuses.map((status) => (
          <li
            key={status.status}
            className='grid grid-cols-[minmax(0,1fr)_11rem_4rem] items-center gap-2 border-t py-2'
          >
            <span className='flex flex-wrap items-center gap-2'>
              {status.label}
              {isShort(status, statuses) && <Badge variant='warning'>all the store has</Badge>}
              {status.picked === 0 && (
                <span className='text-xs text-muted-foreground'>no order with a matching email</span>
              )}
            </span>
            <code className='text-xs text-muted-foreground'>{status.status}</code>
            <span className='text-right tabular-nums'>{status.picked}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Building({ job }: { job: OrderSampleJob }) {
  const scanned = job.daysDone >= job.daysTotal;
  return (
    <div className='space-y-2'>
      <div className='flex justify-between text-sm'>
        <span>
          {scanned
            ? 'Picking orders and loading their details…'
            : `Scanning orders: ${job.daysDone} of ${job.daysTotal} days`}
        </span>
      </div>
      <Progress value={Math.round((job.daysDone / Math.max(job.daysTotal, 1)) * 100)} />
      <p className='text-xs text-muted-foreground'>
        You can leave this tab. The sheet keeps building and shows up here when it's done.
      </p>
    </div>
  );
}

interface FinishedProps {
  job: OrderSampleJob;
  busy: boolean;
  onDownload: () => void;
  onRebuild: () => void;
}

function Finished({ job, busy, onDownload, onRebuild }: FinishedProps) {
  const finishedAt = job.finishedAt ?? job.updatedAt;
  return (
    <div className='space-y-4'>
      <p className='text-xs text-muted-foreground'>
        Built {formatIst(finishedAt)}
        {job.startedBy ? ` by ${job.startedBy}` : ''}
        {job.window ? `, from orders placed ${job.window.from} to ${job.window.to}` : ''}
      </p>
      <div className='grid grid-cols-3 gap-3'>
        <Metric label='Orders scanned' value={job.scanned} />
        <Metric label='Orders in the sheet' value={job.picked} />
        <Metric label='Statuses found' value={job.statuses.length} />
      </div>
      <StatusList statuses={job.statuses} />
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='flex max-w-md items-start gap-1.5 text-xs text-muted-foreground'>
          <Lock className='mt-0.5 h-3.5 w-3.5 shrink-0' />
          Contains customer names and emails. Kim staff only. Available until{' '}
          {formatIst(new Date(finishedAt).getTime() + SHEET_LIFETIME_MS)}.
        </p>
        <div className='flex gap-2'>
          <Button variant='outline' onClick={onRebuild} disabled={busy}>
            <RefreshCw className='h-4 w-4' /> Create a new one
          </Button>
          <Button onClick={onDownload} disabled={busy}>
            <Download className='h-4 w-4' /> Download .xlsx
          </Button>
        </div>
      </div>
    </div>
  );
}

function Failed({ job, busy, onRetry }: { job: OrderSampleJob; busy: boolean; onRetry: () => void }) {
  return (
    <div className='space-y-3 rounded-md border border-red-border p-4'>
      <p className='flex items-start gap-2 text-sm text-red-foreground'>
        <AlertCircle className='mt-0.5 h-4 w-4 shrink-0' />
        Couldn't build the sheet. {job.error}
      </p>
      <Button variant='outline' onClick={onRetry} disabled={busy}>
        Try again
      </Button>
    </div>
  );
}

/**
 * The account's "Test orders" tab: ~100 real orders of one Shopify store, spread evenly over
 * every order status it has, for testing "Where is my order?" in the playground. machine builds
 * the sheet; this tab starts it, follows it and downloads it.
 */
export function TestOrdersSection() {
  const { data: stores = [], isLoading, error } = useOrderSampleStores();
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);
  const store = stores.find((each) => each.storeId === selectedStoreId) ?? stores[0] ?? null;
  const { job, start, download } = useOrderSample(store);
  const [busy, setBusy] = useState(false);

  const run = (action: () => Promise<unknown>, fallback: string) => async () => {
    setBusy(true);
    try {
      await action();
    } catch (actionError) {
      console.error('TestOrdersSection: action failed', actionError);
      toast.error(errorMessage(actionError, fallback));
    } finally {
      setBusy(false);
    }
  };
  const onStart = run(start, "Couldn't start the sheet.");
  const onDownload = run(download, "Couldn't download the sheet.");

  if (isLoading) return <Skeleton className='h-48 w-full' />;

  return (
    <Card className='space-y-5 p-6'>
      <div>
        <h3 className='text-base font-medium'>Test orders for “Where is my order?”</h3>
        <p className='text-sm text-muted-foreground'>
          Picks 100 real orders from the last 30 days, spread evenly across every order status the
          store has, one order per customer.
        </p>
      </div>

      {error ? (
        <p className='text-sm text-red-foreground'>
          Couldn't load this account's Shopify stores. {errorMessage(error, '')}
        </p>
      ) : stores.length === 0 ? (
        <p className='text-sm text-muted-foreground'>
          This account has no Shopify store connected. Connect Shopify for a brand on the
          Integrations page first.
        </p>
      ) : (
        <>
          <div className='flex flex-wrap items-center gap-2'>
            <Select
              value={store ? String(store.storeId) : undefined}
              onValueChange={(value) => setSelectedStoreId(Number(value))}
            >
              <SelectTrigger className='min-w-72' aria-label='Shopify store'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {stores.map((each) => (
                  <SelectItem key={each.storeId} value={String(each.storeId)}>
                    {storeLabel(each)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!job && (
              <Button onClick={onStart} disabled={busy}>
                <FileSpreadsheet className='h-4 w-4' /> Create test order sheet
              </Button>
            )}
          </div>

          {job && isBuilding(job) && <Building job={job} />}
          {job?.status === 'done' && (
            <Finished job={job} busy={busy} onDownload={onDownload} onRebuild={onStart} />
          )}
          {job?.status === 'failed' && <Failed job={job} busy={busy} onRetry={onStart} />}
        </>
      )}
    </Card>
  );
}
